/**
 * Column Configuration
 *
 * Column metadata and configuration for the vulnerabilities table.
 * Centralizes column definitions, widths, and default visibility settings.
 *
 * ============================================================================
 * MIGRATION NOTICE: Column widths moved to central registry (Day 5 - 2025-10-23)
 * ============================================================================
 *
 * Column widths are now managed centrally in:
 * @see src/components/table/columns/registry.ts
 *
 * Use getColumnWidth('risk', columnId) instead of COLUMN_WIDTHS
 *
 * Migration changes:
 * - Date columns (created/visited): 180px → 60px (standardized with other tables)
 * - All other columns: Unchanged
 *
 * This file now contains:
 * - COLUMN_WIDTHS constant: DEPRECATED, maintained for backward compatibility
 * - COLUMN_LABELS: Active, used by column definitions
 * - SORTABLE_COLUMNS: Active, defines sortable columns
 * - COPYABLE_COLUMNS: Active, defines copyable columns
 * - FIXED_COLUMNS: Active, defines required columns
 *
 * To complete migration:
 * 1. Search codebase for COLUMN_WIDTHS usage
 * 2. Replace with getColumnWidth('risk', columnId).fixedWidth
 * 3. Remove COLUMN_WIDTHS constant when no longer referenced
 * ============================================================================
 */

import { VulnerabilityColumnId } from '@/sections/vulnerabilities/types';

/**
 * Column width configuration in pixels
 * All columns use fixed widths for consistent table sizing
 *
 * @deprecated Use getColumnWidth('risk', columnId) from central registry instead
 * @see src/components/table/columns/registry.ts
 *
 * Maintained for backward compatibility until all references are migrated.
 *
 * Migration path:
 * ```typescript
 * // Old (deprecated):
 * import { COLUMN_WIDTHS } from './columns';
 * fixedWidth: COLUMN_WIDTHS.status
 *
 * // New (centralized):
 * import { getColumnWidth } from '@/components/table/columns';
 * fixedWidth: getColumnWidth('risk', 'status').fixedWidth
 * ```
 */
export const COLUMN_WIDTHS: Record<VulnerabilityColumnId, number> = {
  status: 150,
  priority: 120, // Severity column
  name: 300, // Vulnerability name
  cvss: 100,
  epss: 100,
  kev: 100,
  exploit: 140,
  origin: 140,
  tags: 80, // User-defined tags
  ticket: 160, // ITSM ticket column
  created: 180, // First Seen (timestamp) - OUTDATED: Registry uses 60px
  visited: 180, // Last Seen (timestamp) - OUTDATED: Registry uses 60px
  guid: 140,
};

/**
 * Column labels for display
 */
export const COLUMN_LABELS: Record<VulnerabilityColumnId, string> = {
  status: 'Status',
  priority: 'Severity',
  name: 'Vulnerability',
  cvss: 'CVSS',
  epss: 'EPSS',
  kev: 'CISA KEV',
  exploit: 'Public Exploits',
  origin: 'Origin',
  tags: 'Tags',
  ticket: 'Ticket',
  created: 'First Seen',
  visited: 'Last Seen',
  guid: 'ID',
};

/**
 * Columns that support sorting
 */
export const SORTABLE_COLUMNS: VulnerabilityColumnId[] = ['status'];

/**
 * Columns that support copy-to-clipboard
 */
export const COPYABLE_COLUMNS: VulnerabilityColumnId[] = [
  'cvss',
  'epss',
  'created',
  'visited',
];

/**
 * Columns that cannot be disabled or hidden
 */
export const FIXED_COLUMNS: VulnerabilityColumnId[] = ['name'];
